import Title from "../../ui/title/Title";
import { SectionContainer } from "../../ui/grid";
import styles from './CardPayment.module.scss'
import { useCallback, useEffect, useState } from "react";
import { getPaymentMethods, pay, PaymentInfoTypeResp, PaymentMethodsType } from "api/query/Payment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { calculateSubscription, getSubscription, subscriptionPay } from "api/query/Subscription";
import Layout from '../../layout'
import { FiscalCheck } from "../../FiscalCheck";
import { InfoType } from "../../../api/query/Info";

const REDIRECT_STORAGE_PREFIX = 'parking-pay:redirect-url:';
const REDIRECT_STORAGE_INDEX_KEY = 'parking-pay:redirect-url:index';
const REDIRECT_STORAGE_LIMIT = 5;

const CardPaymentPage = ({ paymentType }: { paymentType: PaymentInfoTypeResp }) => {
  const navigate = useNavigate();
  const { zone, sessionID } = useParams();
  const [searchParams] = useSearchParams();
  const grzParam = searchParams.get("grz");
  const phoneParamRaw = searchParams.get("phone");
  const phoneParam = phoneParamRaw && phoneParamRaw !== 'Не указан' ? phoneParamRaw : undefined;
  const timestartParamRaw = searchParams.get("timestart");
  const timestartParam = timestartParamRaw && timestartParamRaw !== '' ? timestartParamRaw : undefined;
  const timeendParamRaw = searchParams.get("timeend");
  const timeendParam = timeendParamRaw && timeendParamRaw !== '' ? timeendParamRaw : undefined;
  const quantityParamRaw = Number(searchParams.get("quantity") || 1);
  const quantityParam = Number.isFinite(quantityParamRaw) ? Math.max(1, quantityParamRaw) : 1;
  const promoCodeParam = searchParams.get("promoCode") || '';
  const subscriptionGrzList = (searchParams.get("subscriptionGrz") || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);

  const [emailValue, setEmailValue] = useState('');
  const [emailValidState, setEmailValidState] = useState(true);
  const [doQueryState, setDoQueryState] = useState<boolean>(false);
  const [payErrorState, setPayErrorState] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<number>(0);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsType>();
  const [subscriptionV2EnabledState, setSubscriptionV2EnabledState] = useState<boolean>(false);
  const isSubscriptionPromoEnabled = subscriptionV2EnabledState;

  const handleInfoLoaded = useCallback((info: InfoType) => {
    setSubscriptionV2EnabledState(!!info.result.subscriptionV2Enabled);
  }, []);

  const radioChangeHandler = (key: number) => {
    setSelectedMethod(key);
  };

  useEffect(() => {
    getPaymentMethods(zone).then((data) => {
      setPaymentMethods(data);
    });
  }, [zone]);

  const paymentMethodsList = paymentMethods && Array.isArray(paymentMethods.result) ? paymentMethods.result : [];
  const selectedPaymentMethod = paymentMethodsList[selectedMethod];
  const isPaymentMethodsLoaded = !!paymentMethods;
  const isPaymentMethodsEmpty = isPaymentMethodsLoaded && paymentMethodsList.length === 0;
  const isQR = selectedPaymentMethod?.type === 'QR';

  const savePaymentRedirect = (redirectURL: string) => {
    const paymentID = redirectURL.split('/')[3]?.split('?')[0];
    if (!paymentID) return;

    const key = `${REDIRECT_STORAGE_PREFIX}${paymentID}`;
    window.sessionStorage.setItem(key, redirectURL);

    try {
      const storedIndex = window.sessionStorage.getItem(REDIRECT_STORAGE_INDEX_KEY);
      const index: string[] = storedIndex ? JSON.parse(storedIndex) : [];
      const nextIndex = [paymentID, ...index.filter((item) => item !== paymentID)].slice(0, REDIRECT_STORAGE_LIMIT);
      index
        .filter((item) => !nextIndex.includes(item))
        .forEach((item) => window.sessionStorage.removeItem(`${REDIRECT_STORAGE_PREFIX}${item}`));
      window.sessionStorage.setItem(REDIRECT_STORAGE_INDEX_KEY, JSON.stringify(nextIndex));
    } catch {
      // Redirect cache is only a convenience for returning to QR payment.
    }
  };

  const openPaymentRedirect = (redirectURL: string, isQrPayment: boolean, includeQrType = false) => {
    savePaymentRedirect(redirectURL);
    const paymentID = redirectURL.split('/')[3]?.split('?')[0];
    if (isQrPayment && paymentID) {
      const typeParam = includeQrType ? '&type=qr' : '';
      navigate(`../../${zone}/callback/SuccessPage.html?paymentID=${paymentID}${typeParam}&redirecting=1`);
      return;
    }
    window.location.href = redirectURL;
  };

  const doPay = async () => {
    if (!(selectedPaymentMethod && sessionID) || doQueryState || !emailValidState) return;
    setDoQueryState(true);
    setPayErrorState('');
    const paymentName = selectedPaymentMethod.paymentName;

    try {
      let redirectURL = '';

      if (paymentType === PaymentInfoTypeResp.abonnement) {
        if (!grzParam) return;
        const subscriptionData = await getSubscription(zone, grzParam, phoneParam);
        const subscription = Array.isArray(subscriptionData.result)
          ? subscriptionData.result.filter((item) => (item.code === sessionID))
          : [];
        if (!(subscription && subscription[0])) return;
        const phoneForRequest = phoneParam ?? 'Не указан';
        const timestartForRequest = timestartParam ?? subscription[0].timestart;
        let calculated = null;

        if (isSubscriptionPromoEnabled) {
          try {
            const calculateData = await calculateSubscription(zone, {
              grz: grzParam,
              phone: phoneForRequest,
              promoCode: promoCodeParam.trim(),
              permissionKindCode: sessionID,
              quantity: quantityParam,
              timestart: timestartForRequest,
            });
            calculated = calculateData.result;
          } catch {
            calculated = null;
          }
        }

        if (isSubscriptionPromoEnabled && (!calculated || !calculated.ok || typeof calculated.amount !== 'number') && (quantityParam !== 1 || promoCodeParam.trim())) {
          setPayErrorState('Не удалось рассчитать стоимость абонемента. Проверьте промокод или попробуйте еще раз.');
          return;
        }

        const amount = calculated?.amount ?? subscription[0].price;
        const timestart = calculated?.timestart ?? timestartForRequest;
        const timeend = calculated?.timeend ?? timeendParam ?? subscription[0].timeend;
        const promoAppliedQuantity = calculated?.promoAppliedQuantity ?? 0;
        const payData = await subscriptionPay(
            zone,
            grzParam,
            phoneForRequest,
            paymentName,
            sessionID,
            amount,
            timestart,
            (isSubscriptionPromoEnabled || subscriptionGrzList.length > 0) ? {
              ...(isSubscriptionPromoEnabled ? {
                totalSum: amount,
                timeend,
                quantity: quantityParam,
                promoCode: promoCodeParam.trim(),
                activationsPromoCode: promoAppliedQuantity,
              } : {}),
              grzList: subscriptionGrzList,
            } : undefined
        );
        redirectURL = payData.result?.redirectURL;
      } else if (paymentType === PaymentInfoTypeResp.session) {
        const isUseFiscal = emailValue !== '' ? emailValue : undefined
        const payData = await pay(zone, sessionID, paymentName, isUseFiscal, grzParam);
        redirectURL = payData.result?.redirectURL;
      }

      if (!redirectURL) {
        setPayErrorState('Не удалось создать платеж. Попробуйте еще раз.');
        return;
      }

      openPaymentRedirect(redirectURL, !!isQR, true);
    } finally {
      setDoQueryState(false);
    }
  }

  return (
    <Layout infoListener={handleInfoLoaded}>
      <SectionContainer fixHeight>
        <div className={styles.btnWrap}>
          <div className={styles.wrapItem}>
            <Title text="Выберите способ оплаты" />
            <div className={styles.items}>
              {isPaymentMethodsEmpty ? (
                <div className={styles.empty}>
                  Способы оплаты недоступны. Попробуйте позже или обратитесь к оператору.
                </div>
              ) : null}
              {paymentMethodsList.map((method: any, key: number) =>
                <div
                  key={key}
                  className={`${styles.item} ${selectedMethod === key ? styles.item__active : ''}`}
                  onClick={() => { radioChangeHandler(key) }}
                >
                  <label className={styles.label_wrap} onClick={() => { radioChangeHandler(key) }}>
                    <input type="radio" checked={selectedMethod === key} className={`${key}`} />
                    <span className={`${key} ${selectedMethod === key ? styles.radio__active : styles.radio}`} />
                  </label>
                  <img src={method.imgURL} alt="" className={`${key}`} onClick={() => { radioChangeHandler(key) }} />
                  <div className={styles.itemName} onClick={() => { radioChangeHandler(key) }}>{method.paymentViewName}</div>
                  {styles.itemDescription ? (
                    <div className={styles.itemDescription} onClick={() => { radioChangeHandler(key) }}>
                      {method.paymentDescription}
                    </div>
                  ) : null}

                  {selectedMethod === key && method.useFiscal && (method.paymentViewName === "Система Быстрых Платежей" || method.paymentViewName === "Банковская карта") && (
                      <div className={styles.fiscalContainer}>
                        <FiscalCheck setEmailValue={setEmailValue} setEmailValid={setEmailValidState}/>
                      </div>
                  )}

                </div>
              )}

            </div>
          </div>

          <button
            type="button"
            onClick={doPay}
            disabled={doQueryState || !selectedPaymentMethod || !emailValidState}
            className={`${styles.btn} ${doQueryState || !selectedPaymentMethod || !emailValidState ? styles.btn_notactive : ''}`}
          >
            {doQueryState ? (
              <>
                <span className={styles.btn_spinner} />
                <span>Создаем платеж...</span>
              </>
            ) : 'Завершить оплату'}
          </button>
          {payErrorState ? <div className={styles.error}>{payErrorState}</div> : null}
        </div>
      </SectionContainer>
    </Layout>
  )
}

export default CardPaymentPage;
